import React from 'react';
import { format } from 'date-fns';
import { CheckCircle2, Copy, Trash2 } from 'lucide-react';
import { useLanguage } from '../../../../features/language/useLanguage';
import type { VoucherRow } from '../../../../services/subscriptionService';
import { Th } from './primitives';
import { VoucherStatusBadge } from './VoucherStatusBadge';

interface VoucherTableProps {
  rows: VoucherRow[];
  copiedCode: string | null;
  onCopyCode: (e: React.MouseEvent, code: string) => void;
  onOpen: (id: string) => void;
  onDelete: (voucher: VoucherRow) => void;
}

/** The admin voucher list table (rows are clickable to open the audit drawer). */
export function VoucherTable({ rows, copiedCode, onCopyCode, onOpen, onDelete }: VoucherTableProps) {
  const { t } = useLanguage();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left">
          <tr>
            <Th>{t('subscription.voucher.col.code')}</Th>
            <Th>{t('subscription.voucher.col.name')}</Th>
            <Th>{t('subscription.voucher.col.duration')}</Th>
            <Th>{t('subscription.voucher.col.expires')}</Th>
            <Th>{t('subscription.voucher.col.usage')}</Th>
            <Th>{t('subscription.voucher.col.status')}</Th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((v) => (
            <tr
              key={v.id}
              data-test-id={`voucher-row-${v.id}`}
              className="hover:bg-gray-50/60 cursor-pointer transition-colors"
              onClick={() => onOpen(v.id)}
            >
              <td className="px-4 py-3">
                <button
                  onClick={(e) => onCopyCode(e, v.code)}
                  className="group inline-flex items-center gap-1.5 font-mono text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-gray-900 tracking-wider transition-colors"
                  title="Copy code"
                >
                  <span>{v.code}</span>
                  <div className="relative h-3.5 w-3.5">
                    <CheckCircle2
                      className={`absolute inset-0 h-3.5 w-3.5 text-emerald-600 transition-all duration-300 ${copiedCode === v.code ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}
                    />
                    <Copy
                      className={`absolute inset-0 h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 transition-all duration-300 ${copiedCode === v.code ? 'scale-50 opacity-0' : 'scale-100 opacity-100'}`}
                    />
                  </div>
                </button>
              </td>
              <td className="px-4 py-3 font-medium text-gray-900">{v.name}</td>
              <td className="px-4 py-3 text-gray-700 tabular-nums">
                {v.duration_months}
                {v.duration_months === 1 ? ' Month' : ' Months'}
              </td>
              <td className="px-4 py-3 text-gray-700 tabular-nums">{format(new Date(v.expires_at), 'PP')}</td>
              <td className="px-4 py-3 tabular-nums">
                <span className="font-medium text-gray-900">{v.redemption_count}</span>
                <span className="text-gray-400">
                  {' / '}
                  {v.max_redemptions !== null ? v.max_redemptions : '∞'}
                </span>
              </td>
              <td className="px-4 py-3">
                <VoucherStatusBadge status={v.status} />
              </td>
              <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                {v.status !== 'deleted' && (
                  <button
                    onClick={() => onDelete(v)}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                    title={t('common.delete')}
                    data-test-id={`voucher-delete-${v.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
